import { fetchAuthSession } from "aws-amplify/auth";
import { useAuthStore } from "./auth";

// In development, Vite proxy rewrites /api to localhost:8080/v1
// In production, we call the API directly
const API_BASE = import.meta.env.PROD
  ? (import.meta.env.VITE_API_URL || "https://api-asm.sertua.com/v1")
  : "/api";

/**
 * Try to refresh the authentication token using AWS Amplify.
 * Returns true if refresh was successful, false otherwise.
 */
async function tryRefreshToken(): Promise<boolean> {
  try {
    // Force refresh the session to get a new token
    const session = await fetchAuthSession({ forceRefresh: true });
    const newToken = session.tokens?.idToken?.toString();

    if (newToken) {
      // Update the token in the auth store
      useAuthStore.setState({ token: newToken });
      return true;
    }
    return false;
  } catch (error) {
    console.error("Token refresh failed:", error);
    return false;
  }
}

async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = useAuthStore.getState().token;

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    // Handle 401 - try to refresh token
    if (response.status === 401) {
      // Token might have expired, try to refresh
      const refreshed = await tryRefreshToken();
      if (refreshed) {
        // Retry the request with the new token
        const newToken = useAuthStore.getState().token;
        const retryHeaders: HeadersInit = {
          ...headers,
          ...(newToken && { Authorization: `Bearer ${newToken}` }),
        };
        const retryResponse = await fetch(`${API_BASE}${endpoint}`, {
          ...options,
          headers: retryHeaders,
        });
        if (retryResponse.ok) {
          const contentType = retryResponse.headers.get("content-type");
          if (!contentType || !contentType.includes("application/json")) {
            return {} as T;
          }
          return retryResponse.json() as Promise<T>;
        }
      }
      // Refresh failed or retry failed - redirect to login
      useAuthStore.getState().logout();
      window.location.href = "/login";
      throw new Error("Session expired. Please log in again.");
    }

    // Try to parse error response
    let errorMessage = "API request failed";
    try {
      const errorData = await response.json();
      // Handle nested error format: {error: {code, message}} or flat {message}
      errorMessage = errorData.error?.message || errorData.message || errorMessage;
    } catch {
      // Response might not be JSON
      errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  // Handle empty responses (204 No Content, etc.)
  const contentType = response.headers.get("content-type");
  if (!contentType || !contentType.includes("application/json")) {
    return {} as T;
  }

  return response.json() as Promise<T>;
}

// Scans
export interface Scan {
  id: string;
  target: string;
  modules: string[];
  mode: string;
  status: "pending" | "running" | "completed" | "failed" | "cancelled";
  progress?: {
    current_module: string;
    modules_completed: number;
    modules_total: number;
    percent: number;
    module_status?: "running" | "completed";
  };
  error?: string;
  created_at: string;
  completed_at?: string;
}

export interface ScanResult {
  id: string;
  target: string;
  modules: Record<string, unknown>;
  completed_at: string;
  ai_insights?: AiInsights;
}

// AI Insights Types
export type RiskLevel = "critical" | "high" | "medium" | "low" | "info";

export type FindingType =
  // Certificate issues
  | "certificate_expired"
  | "certificate_expiring_soon"
  | "certificate_weak_signature"
  | "certificate_self_signed"
  | "certificate_hostname_mismatch"
  | "certificate_chain_incomplete"
  // DNS issues
  | "dns_missing_ns"
  | "dns_dangling_cname"
  | "dns_zone_transfer_enabled"
  | "dns_no_sec_dnssec"
  // Email security issues
  | "spf_missing"
  | "spf_too_permissive"
  | "dkim_missing"
  | "dmarc_missing"
  | "dmarc_none"
  | "dmarc_quarantine"
  // HTTP header issues
  | "headers_missing_hsts"
  | "headers_missing_csp"
  | "headers_missing_xfo"
  | "headers_missing_xcto"
  | "headers_server_info_leak"
  | "headers_powered_by_leak"
  // TLS issues
  | "tls_weak_cipher"
  | "tls_old_protocol"
  | "tls_missing_pfs"
  // Exposure issues
  | "exposure_open_port"
  | "exposure_sensitive_port"
  | "exposure_known_vulnerability"
  | "exposure_credential_leak"
  | "exposure_data_breach"
  // Cloud issues
  | "cloud_public_bucket"
  | "cloud_misconfigured_iam"
  | "cloud_open_security_group"
  // Repository issues
  | "repo_secrets_exposed"
  | "repo_sensitive_files"
  // Other
  | "tech_outdated_software"
  | "tech_end_of_life"
  | "unknown";

export interface EpssScore {
  cve: string;
  epss: number;       // 0-1 probability
  percentile: number; // 0-1 percentile rank
  date?: string;
}

export interface ScoreFactors {
  severity: number;
  exploitability: number;
  asset_criticality: number;
  remediation_difficulty: number;
  known_exploited: boolean;
  epss_score?: number;
}

export interface ScoringResult {
  score: number;       // 0-10
  risk_level: RiskLevel;
  factors: ScoreFactors;
  confidence: number;  // 0-1
}

export interface RiskLevelCounts {
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
}

export interface ExtractionSummary {
  total_findings: number;
  by_module: Record<string, number>;
  by_type: Record<string, number>;
  cve_findings: number;
}

export interface ScoringSummary {
  total_findings: number;
  by_risk_level: RiskLevelCounts;
  cve_findings: number;
  epss_enriched: number;
  kev_listed: number;
  avg_score: number;
  max_score: number;
  extraction: ExtractionSummary;
}

export interface ScoredFinding {
  id: string;
  finding_type: FindingType;
  source_module: string;
  asset_id?: string;
  cve_ids: string[];
  custom_severity?: number;
  is_public_facing?: boolean;
  context: Record<string, unknown>;
  title: string;
  description: string;
  issue_code?: string;
  score: ScoringResult;
  rank: number;
  epss?: EpssScore;
  is_kev?: boolean;
}

export type InsightSource = "rule_based" | "local_ml";

export interface UpgradeHint {
  feature: string;
  required_tier: string;
  description: string;
}

export interface AiInsights {
  findings: ScoredFinding[];
  summary: ScoringSummary;
  source: InsightSource;
  upgrade_hint?: UpgradeHint;
}

export interface PaginatedScans {
  scans: Scan[];
  total: number;
  page: number;
  per_page: number;
}

export const scansApi = {
  list: async (params?: { page?: number; per_page?: number; status?: string }): Promise<PaginatedScans> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.per_page) searchParams.set("per_page", params.per_page.toString());
    if (params?.status) searchParams.set("status", params.status);
    return fetchWithAuth<PaginatedScans>(`/scans?${searchParams}`);
  },

  get: (id: string) => fetchWithAuth<Scan>(`/scans/${id}`),

  getResults: (id: string) => fetchWithAuth<ScanResult>(`/scans/${id}/results`),

  create: (data: { target: string; modules: string[]; mode?: "passive" | "self_assessment" }) =>
    fetchWithAuth<Scan>("/scans", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    fetchWithAuth<void>(`/scans/${id}`, { method: "DELETE" }),
};

// Domains
export interface Domain {
  id: string;
  domain: string;
  verification_token: string;
  verification_status: "unverified" | "pending" | "verified" | "failed";
  verification_attempts: number;
  verified: boolean;
  verified_at?: string;
  created_at: string;
  /** Whether active modules (portscan, bruteforce, etc.) are allowed for this domain */
  active_modules_allowed?: boolean;
}

export interface VerificationResponse {
  id: string;
  domain: string;
  status: string;
  verification_method: string;
  verification_record: {
    type: string;
    name: string;
    value: string;
  };
  message: string;
}

export const domainsApi = {
  list: () => fetchWithAuth<Domain[]>("/domains"),

  // Add a domain (starts as 'unverified')
  add: (domain: string) =>
    fetchWithAuth<Domain>("/domains", {
      method: "POST",
      body: JSON.stringify({ domain }),
    }),

  // Legacy alias
  startVerification: (domain: string) =>
    fetchWithAuth<Domain>("/domains", {
      method: "POST",
      body: JSON.stringify({ domain }),
    }),

  // Initiate verification process (starts periodic checks)
  initiateVerification: (domain: string) =>
    fetchWithAuth<VerificationResponse>(`/domains/${domain}/verify/start`, {
      method: "POST",
    }),

  // Retry verification after max attempts reached
  retryVerification: (domain: string) =>
    fetchWithAuth<VerificationResponse>(`/domains/${domain}/verify/retry`, {
      method: "POST",
    }),

  // Manual check verification status
  checkVerification: (domain: string) =>
    fetchWithAuth<Domain>(`/domains/${domain}/verify`),

  delete: (domain: string) =>
    fetchWithAuth<void>(`/domains/${domain}`, { method: "DELETE" }),
};

// IP Intelligence types
export interface IpGeolocation {
  continent_code?: string;
  continent_name?: string;
  country_code?: string;
  country_name?: string;
  region_code?: string;
  region_name?: string;
  city?: string;
  zip?: string;
  latitude?: number;
  longitude?: number;
}

export interface IpSecurityInfo {
  is_proxy: boolean;
  proxy_type?: string;
  is_vpn: boolean;
  is_tor: boolean;
  is_relay: boolean;
  is_crawler: boolean;
  crawler_name?: string;
  is_datacenter: boolean;
  threat_level: "unknown" | "low" | "medium" | "high" | "critical";
  threat_types: string[];
}

export interface IpNetworkInfo {
  isp?: string;
  organization?: string;
  asn?: number;
  as_name?: string;
  connection_type?: string;
}

export interface IpMetadata {
  timezone_id?: string;
  timezone_offset?: string;
  currency_code?: string;
  currency_symbol?: string;
  languages: string[];
  calling_code?: string;
  flag_emoji?: string;
  is_eu?: boolean;
}

export interface RiskFactor {
  category: string;
  description: string;
  score_impact: number;
  severity: "info" | "low" | "medium" | "high" | "critical";
}

export interface IpIntelResult {
  ip: string;
  ip_version: number;
  hostname?: string;
  geolocation: IpGeolocation;
  security: IpSecurityInfo;
  network: IpNetworkInfo;
  metadata: IpMetadata;
  score: number;
  risk_factors: RiskFactor[];
  lookup_time_ms: number;
  cached: boolean;
  provider: string;
}

export interface BulkIpIntelResult {
  results: IpIntelResult[];
  failed: Array<{ ip: string; error: string }>;
  total_requested: number;
  total_time_ms: number;
  cache_hits: number;
}

// Port Scan types
export type Protocol = "tcp" | "udp";
export type PortState = "open" | "closed" | "filtered" | "open_filtered";

/** Detected service information from fingerprinting */
export interface ServiceInfo {
  name: string;
  product?: string;
  version?: string;
  banner?: string;
  extra_info?: string;
  confidence: number;
  method: "port_lookup" | "banner" | "probe" | "tls_cert" | "http_header";
}

/** CVE vulnerability associated with a detected service */
export interface PortVulnerability {
  cve_id: string;
  cvss_score?: number;
  severity: "critical" | "high" | "medium" | "low" | "none";
  description: string;
  /** EPSS score (0-1): probability of exploitation in next 30 days */
  epss_score?: number;
  /** EPSS percentile (0-1): how this CVE ranks compared to others */
  epss_percentile?: number;
  /** Whether this CVE is in CISA's Known Exploited Vulnerabilities catalog */
  is_kev?: boolean;
}

export interface PortInfo {
  port: number;
  protocol: Protocol;
  state: PortState;
  service_hint?: string;
  /** Detailed service fingerprint (product, version, banner) */
  service?: ServiceInfo;
  response_time_ms: number;
  /** CVEs affecting the detected service version */
  vulnerabilities?: PortVulnerability[];
  /** Whether this port has known vulnerabilities */
  is_vulnerable?: boolean;
  /** Highest CVE severity on this port */
  highest_severity?: "critical" | "high" | "medium" | "low" | "none";
  /** Count of critical/high severity CVEs */
  critical_cve_count?: number;
}

export interface FilteredPort {
  port: number;
  protocol: Protocol;
  reason: string;
}

export interface PortScanStats {
  total_ports_scanned: number;
  tcp_open: number;
  tcp_closed: number;
  tcp_filtered: number;
  udp_open: number;
  udp_filtered: number;
  avg_response_time_ms: number;
}

/** Vulnerability summary across all scanned ports */
export interface VulnSummary {
  total_cves: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  vulnerable_ports: number;
  /** CVEs with EPSS > 0.1 (high exploitation risk) */
  high_epss_count: number;
  /** CVEs in CISA KEV catalog */
  kev_count: number;
}

export interface PortScanResult {
  target: string;
  ip?: string;
  source_subdomain?: string;
  tcp_ports: PortInfo[];
  udp_ports: PortInfo[];
  filtered_ports?: FilteredPort[];
  scan_duration_ms: number;
  stats: PortScanStats;
  /** Vulnerability summary (only present when CVE enrichment is enabled) */
  vuln_summary?: VulnSummary;
}

/** Multi-IP port scan result (when scanning all IPs from DNS discovery) */
export interface MultiIpPortScanResult {
  source_domain: string;
  scanned_ips: number;
  total_requested: number;
  results: PortScanResult[];
  aggregate_stats: {
    total_tcp_open: number;
    total_udp_open: number;
    unique_tcp_ports: number[];
    unique_udp_ports: number[];
    total_scan_duration_ms: number;
  };
  /** Aggregate vulnerability summary across all IPs */
  aggregate_vuln_summary?: VulnSummary;
}

// Quick lookups
export const lookupApi = {
  dns: (domain: string) => fetchWithAuth<unknown>(`/lookup/dns/${domain}`),

  subdomains: (domain: string) =>
    fetchWithAuth<unknown>(`/lookup/dns/${domain}/subdomains`),

  email: (domain: string) => fetchWithAuth<unknown>(`/lookup/email/${domain}`),

  whois: (domain: string) => fetchWithAuth<unknown>(`/lookup/whois/${domain}`),

  tls: (domain: string) => fetchWithAuth<unknown>(`/lookup/tls/${domain}`),

  headers: (domain: string) =>
    fetchWithAuth<unknown>(`/lookup/headers/${domain}`),

  tech: (domain: string) => fetchWithAuth<unknown>(`/lookup/tech/${domain}`),

  repos: (target: string) => fetchWithAuth<unknown>(`/lookup/repos/${target}`),

  crawlHints: (domain: string) =>
    fetchWithAuth<unknown>(`/lookup/crawl-hints/${domain}`),

  jsAnalysis: (domain: string) =>
    fetchWithAuth<unknown>(`/lookup/js/${domain}`),

  favicon: (domain: string) =>
    fetchWithAuth<unknown>(`/lookup/favicon/${domain}`),

  // IP Intelligence lookups
  ip: (ip: string) => fetchWithAuth<IpIntelResult>(`/lookup/ip/${ip}`),

  ipSecurity: (ip: string) =>
    fetchWithAuth<IpSecurityInfo>(`/lookup/ip/${ip}/security`),

  ipBulk: (ips: string[]) =>
    fetchWithAuth<BulkIpIntelResult>("/lookup/ip/bulk", {
      method: "POST",
      body: JSON.stringify({ ips }),
    }),

  // Port Scan lookup
  portscan: (target: string) =>
    fetchWithAuth<PortScanResult>(`/lookup/portscan/${target}`),
};

// User
export interface UserProfile {
  id: string;
  org_id: string;
  org_name: string;
  email: string;
  name?: string;
  role: string;
  created_at: string;
}

export interface TeamMember {
  id: string;
  email: string;
  name?: string;
  role: string;
  created_at: string;
}

export interface Invitation {
  id: string;
  email: string;
  role: string;
  expires_at: string;
  created_at: string;
}

export interface PendingInvitation {
  id: string;
  org_id: string;
  org_name: string;
  role: string;
  invited_by_email?: string;
  expires_at: string;
}

// Helper: all /v1/users/* endpoints wrap responses in { success, data }
interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

export const userApi = {
  getProfile: () =>
    fetchWithAuth<ApiEnvelope<UserProfile>>("/users/me").then((r) => r.data),

  updateProfile: (data: { name?: string }) =>
    fetchWithAuth<ApiEnvelope<UserProfile>>("/users/me", {
      method: "PUT",
      body: JSON.stringify(data),
    }).then((r) => r.data),

  listMembers: () =>
    fetchWithAuth<ApiEnvelope<TeamMember[]>>("/users").then((r) => r.data),

  inviteUser: (req: { email: string; role?: string }) =>
    fetchWithAuth<ApiEnvelope<Invitation>>("/users/invite", {
      method: "POST",
      body: JSON.stringify(req),
    }).then((r) => r.data),

  listInvitations: () =>
    fetchWithAuth<ApiEnvelope<Invitation[]>>("/users/invitations").then((r) => r.data),

  cancelInvitation: (id: string) =>
    fetchWithAuth<void>(`/users/invitations/${id}`, { method: "DELETE" }),

  removeUser: (id: string) =>
    fetchWithAuth<void>(`/users/${id}`, { method: "DELETE" }),

  updateUserRole: (id: string, role: string) =>
    fetchWithAuth<void>(`/users/${id}/role`, {
      method: "PUT",
      body: JSON.stringify({ role }),
    }),

  renameOrganization: (name: string) =>
    fetchWithAuth<ApiEnvelope<{ id: string; name: string }>>("/organization/name", {
      method: "PUT",
      body: JSON.stringify({ name }),
    }).then((r) => r.data),

  getPendingInvitation: () =>
    fetchWithAuth<ApiEnvelope<PendingInvitation | null>>("/users/me/pending-invitation").then((r) => r.data),

  acceptInvitation: () =>
    fetchWithAuth<ApiEnvelope<{ org_id: string; org_name: string; role: string; message: string }>>("/users/me/accept-invitation", {
      method: "POST",
    }).then((r) => r.data),

  declineInvitation: () =>
    fetchWithAuth<void>("/users/me/decline-invitation", {
      method: "POST",
    }),
};


// Comparisons
export interface ComparisonSummary {
  headline: string;
  key_changes: string[];
  recommendations: string[];
}

export interface ChangeResponse {
  change_type: string;
  description: string;
  severity: "improvement" | "regression" | "neutral" | "info";
  field_path?: string;
  old_value?: unknown;
  new_value?: unknown;
}

export interface ModuleDiffResponse {
  module: string;
  present_in_a: boolean;
  present_in_b: boolean;
  score_change?: number;
  old_score?: number;
  new_score?: number;
  improvement_count: number;
  regression_count: number;
  changes: ChangeResponse[];
}

export interface TimeDeltaResponse {
  days: number;
  hours: number;
  human_readable: string;
}

export interface ComparisonResponse {
  id: string;
  scan_a_id: string;
  scan_b_id: string;
  target: string;
  overall_score_change?: number;
  overall_assessment: "improved" | "degraded" | "stable" | "mixed";
  improvement_count: number;
  regression_count: number;
  summary?: ComparisonSummary;
  created_at: string;
}

export interface DiffResponse {
  comparison: ComparisonResponse;
  modules: ModuleDiffResponse[];
  time_delta: TimeDeltaResponse;
}

export interface ChangeHistoryEntry {
  id: string;
  module: string;
  change_type: string;
  severity: string;
  description: string;
  field_path?: string;
  is_regression: boolean;
  is_improvement: boolean;
  detected_at: string;
}

export interface ChangeHistoryResponse {
  changes: ChangeHistoryEntry[];
  total: number;
  page: number;
  per_page: number;
}

export interface TrendDataPoint {
  scan_id?: string;
  date: string;
  overall_score?: number;
  grade?: string;
  module_scores: Record<string, number>;
}

export interface TrendSummary {
  data_points: number;
  min_score?: number;
  max_score?: number;
  avg_score?: number;
  latest_score?: number;
  latest_grade?: string;
  first_date?: string;
  last_date?: string;
}

export interface TrendResponse {
  target: string;
  data_points: TrendDataPoint[];
  summary?: TrendSummary;
}

export interface RegressionItem {
  target: string;
  latest_scan_id: string;
  regression_count: number;
  overall_score_change?: number;
  comparison_date: string;
  scan_completed_at?: string;
}

export interface RegressionListResponse {
  regressions: RegressionItem[];
}

export const comparisonsApi = {
  /** Compare two specific scans */
  compareScans: (scanAId: string, scanBId: string) =>
    fetchWithAuth<DiffResponse>(`/scans/compare?scan_a=${scanAId}&scan_b=${scanBId}`),

  /** Compare a scan with its previous scan for the same target */
  compareWithPrevious: (scanId: string) =>
    fetchWithAuth<DiffResponse>(`/scans/${scanId}/compare-previous`),

  /** Get change history for a target domain */
  getTargetHistory: (domain: string, params?: { page?: number; per_page?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.per_page) searchParams.set("per_page", params.per_page.toString());
    return fetchWithAuth<ChangeHistoryResponse>(`/targets/${domain}/history?${searchParams}`);
  },

  /** Get score trends for a target domain */
  getTargetTrends: (domain: string, days?: number) => {
    const searchParams = new URLSearchParams();
    if (days) searchParams.set("days", days.toString());
    return fetchWithAuth<TrendResponse>(`/targets/${domain}/trends?${searchParams}`);
  },

  /** List targets with recent regressions */
  listRegressions: (days?: number) => {
    const searchParams = new URLSearchParams();
    if (days) searchParams.set("days", days.toString());
    return fetchWithAuth<RegressionListResponse>(`/targets/regressions?${searchParams}`);
  },
};

// Schedules
export interface Schedule {
  id: string;
  domain_id: string;
  name: string;
  enabled: boolean;
  cron_expression: string | null;
  interval_minutes: number | null;
  modules: string[];
  mode: string;
  webhook_url: string | null;
  preferred_hour: number | null;
  preferred_day_of_week: number | null;
  timezone: string;
  last_run_at: string | null;
  last_run_status: string | null;
  next_run_at: string;
  consecutive_failures: number;
  auto_compare: boolean;
  last_comparison_id: string | null;
  created_at: string;
}

export interface ScheduleListResponse {
  schedules: Schedule[];
  total: number;
  page: number;
  per_page: number;
}

export interface CreateScheduleRequest {
  domain_id: string;
  name: string;
  frequency: string;
  cron_expression?: string;
  modules?: string[];
  mode?: string;
  webhook_url?: string;
  preferred_hour?: number;
  preferred_day_of_week?: number;
  timezone?: string;
  auto_compare?: boolean;
}

export interface UpdateScheduleRequest {
  name?: string;
  enabled?: boolean;
  frequency?: string;
  cron_expression?: string;
  modules?: string[];
  mode?: string;
  webhook_url?: string;
  preferred_hour?: number;
  preferred_day_of_week?: number;
  timezone?: string;
  auto_compare?: boolean;
}

export interface ScheduleExecution {
  id: string;
  schedule_id: string;
  scan_id: string | null;
  triggered_at: string;
  scheduled_for: string;
  status: string;
  skip_reason: string | null;
}

export interface ExecutionListResponse {
  executions: ScheduleExecution[];
  total: number;
  page: number;
  per_page: number;
}

export interface QuotaResponse {
  tier: string;
  year_month: string;
  scheduled_scans_used: number;
  manual_scans_used: number;
  total_used: number;
  limit: number | null;
  remaining: number | null;
  percentage_used: number | null;
}

export interface TierLimitsResponse {
  tier: string;
  min_interval_minutes: number;
  max_schedules: number | null;
  allowed_frequencies: string[];
  supports_custom_cron: boolean;
  asset_inventory_enabled: boolean;
  webhooks_enabled: boolean;
}

export const schedulesApi = {
  /** List all schedules for the organization */
  list: (params?: { page?: number; per_page?: number; domain_id?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.per_page) searchParams.set("per_page", params.per_page.toString());
    if (params?.domain_id) searchParams.set("domain_id", params.domain_id);
    return fetchWithAuth<ScheduleListResponse>(`/schedules?${searchParams}`);
  },

  /** Get a single schedule by ID */
  get: (id: string) => fetchWithAuth<Schedule>(`/schedules/${id}`),

  /** Create a new schedule */
  create: (data: CreateScheduleRequest) =>
    fetchWithAuth<Schedule>("/schedules", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  /** Update an existing schedule */
  update: (id: string, data: UpdateScheduleRequest) =>
    fetchWithAuth<Schedule>(`/schedules/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  /** Delete a schedule */
  delete: (id: string) =>
    fetchWithAuth<void>(`/schedules/${id}`, { method: "DELETE" }),

  /** Pause a schedule */
  pause: (id: string) =>
    fetchWithAuth<Schedule>(`/schedules/${id}/pause`, { method: "POST" }),

  /** Resume a schedule */
  resume: (id: string) =>
    fetchWithAuth<Schedule>(`/schedules/${id}/resume`, { method: "POST" }),

  /** Manually trigger a schedule */
  trigger: (id: string) =>
    fetchWithAuth<ScheduleExecution>(`/schedules/${id}/trigger`, { method: "POST" }),

  /** Get execution history for a schedule */
  getExecutions: (id: string, params?: { page?: number; per_page?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.per_page) searchParams.set("per_page", params.per_page.toString());
    return fetchWithAuth<ExecutionListResponse>(`/schedules/${id}/executions?${searchParams}`);
  },

  /** Get current quota status */
  getQuota: () => fetchWithAuth<QuotaResponse>("/quota"),

  /** Get tier limits for the organization */
  getTierLimits: () => fetchWithAuth<TierLimitsResponse>("/quota/limits"),
};

// Assets
export interface Asset {
  id: string;
  asset_type: "subdomain" | "ip" | "port" | "certificate" | "cloud";
  identifier: string;
  status: "active" | "stale" | "removed";
  visibility?: "known" | "unknown" | "rogue";
  first_seen_at: string;
  last_seen_at: string;
  parent_asset_id?: string;
  root_domain_id?: string;
  metadata: Record<string, unknown>;
}

export interface AssetSubdomainDetails {
  fqdn: string;
  record_types: string[];
  cname_target?: string;
  discovery_source?: string;
  is_wildcard: boolean;
  takeover_risk?: string;
  http_status?: number;
  http_title?: string;
}

export interface AssetIpDetails {
  ip_address: string;
  ip_version: number;
  reverse_dns?: string;
  asn?: number;
  asn_name?: string;
  country_code?: string;
  country_name?: string;
  city?: string;
  is_cloud: boolean;
  cloud_provider?: string;
  cloud_region?: string;
}

export interface AssetPortDetails {
  port: number;
  protocol: string;
  service_name?: string;
  service_version?: string;
  service_banner?: string;
  is_encrypted: boolean;
  tls_version?: string;
  state: string;
  // Service probe enrichment fields
  service_product?: string;
  http_server?: string;
  http_title?: string;
  http_technologies?: string[];
  probe_method?: string;
  probe_confidence?: number;
  infrastructure?: string;
}

export interface AssetWithDetails extends Asset {
  subdomain?: AssetSubdomainDetails;
  ip?: AssetIpDetails;
  port?: AssetPortDetails;
}

export interface AssetListResponse {
  items: Asset[];
  total: number;
  page: number;
  per_page: number;
}

export interface AssetStats {
  total_count: number;
  active_count: number;
  stale_count: number;
  subdomains_count: number;
  ips_count: number;
  ports_count: number;
  new_this_week: number;
}

export interface AssetHistoryEntry {
  id: string;
  change_type: string;
  field_name?: string;
  old_value?: string;
  new_value?: string;
  scan_id?: string;
  created_at: string;
}

export interface AssetListParams {
  asset_type?: string;
  status?: string;
  root_domain_id?: string;
  search?: string;
  is_new?: boolean;
  exclude_excepted?: boolean;
  page?: number;
  per_page?: number;
}

/** Related assets (parent and children) for an asset */
export interface RelatedAssets {
  parent: Asset | null;
  children: Asset[];
}

/** Asset group summary for a domain */
export interface DomainAssetGroup {
  domain_id: string;
  domain_name: string;
  total_count: number;
  subdomain_count: number;
  ip_count: number;
  port_count: number;
}

export const assetsApi = {
  /** List all assets with optional filtering */
  list: (params?: AssetListParams) => {
    const searchParams = new URLSearchParams();
    if (params?.asset_type) searchParams.set("asset_type", params.asset_type);
    if (params?.status) searchParams.set("status", params.status);
    if (params?.root_domain_id) searchParams.set("root_domain_id", params.root_domain_id);
    if (params?.search) searchParams.set("search", params.search);
    if (params?.is_new) searchParams.set("is_new", "true");
    if (params?.exclude_excepted) searchParams.set("exclude_excepted", "true");
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.per_page) searchParams.set("per_page", params.per_page.toString());
    return fetchWithAuth<AssetListResponse>(`/assets?${searchParams}`);
  },

  /** Get asset statistics */
  getStats: () => fetchWithAuth<AssetStats>("/assets/stats"),

  /** Get a single asset by ID with full details */
  get: (id: string) => fetchWithAuth<AssetWithDetails>(`/assets/${id}`),

  /** Get asset history */
  getHistory: (id: string) => fetchWithAuth<AssetHistoryEntry[]>(`/assets/${id}/history`),

  /** List subdomains only */
  listSubdomains: (params?: Omit<AssetListParams, "asset_type">) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set("status", params.status);
    if (params?.root_domain_id) searchParams.set("root_domain_id", params.root_domain_id);
    if (params?.search) searchParams.set("search", params.search);
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.per_page) searchParams.set("per_page", params.per_page.toString());
    return fetchWithAuth<AssetListResponse>(`/assets/subdomains?${searchParams}`);
  },

  /** List IP addresses only */
  listIps: (params?: Omit<AssetListParams, "asset_type">) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set("status", params.status);
    if (params?.root_domain_id) searchParams.set("root_domain_id", params.root_domain_id);
    if (params?.search) searchParams.set("search", params.search);
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.per_page) searchParams.set("per_page", params.per_page.toString());
    return fetchWithAuth<AssetListResponse>(`/assets/ips?${searchParams}`);
  },

  /** Delete an asset */
  delete: (id: string) =>
    fetchWithAuth<void>(`/assets/${id}`, { method: "DELETE" }),

  /** Update asset status */
  updateStatus: (id: string, status: "active" | "stale" | "removed") =>
    fetchWithAuth<void>(`/assets/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),

  /** Get related assets (parent and children) */
  getRelatedAssets: (id: string) =>
    fetchWithAuth<RelatedAssets>(`/assets/${id}/related`),

  /** Get assets grouped by domain */
  getGrouped: () => fetchWithAuth<DomainAssetGroup[]>("/assets/grouped"),

  /** List assets for a specific domain */
  listByDomain: (domainId: string, params?: Omit<AssetListParams, "root_domain_id">) => {
    const searchParams = new URLSearchParams();
    if (params?.asset_type) searchParams.set("asset_type", params.asset_type);
    if (params?.status) searchParams.set("status", params.status);
    if (params?.search) searchParams.set("search", params.search);
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.per_page) searchParams.set("per_page", params.per_page.toString());
    return fetchWithAuth<AssetListResponse>(`/domains/${domainId}/assets?${searchParams}`);
  },

  /** Get asset stats for a specific domain */
  getStatsByDomain: (domainId: string) =>
    fetchWithAuth<AssetStats>(`/domains/${domainId}/assets/stats`),
};

// Asset Exception Types
export interface AssetException {
  id: string;
  asset_type: string;
  identifier: string;
  excluded_modules: string[];
  reason?: string;
  created_by?: string;
  asset_id?: string;
  created_at: string;
  updated_at: string;
}

export interface AssetExceptionListResponse {
  items: AssetException[];
  total: number;
  page: number;
  per_page: number;
}

export interface CreateExceptionParams {
  asset_type: string;
  identifier: string;
  excluded_modules: string[];
  reason?: string;
}

export interface ExceptAssetParams {
  excluded_modules: string[];
  reason?: string;
}

export interface UpdateExceptionParams {
  excluded_modules?: string[];
  reason?: string;
}

export interface ExceptionListParams {
  asset_type?: string;
  search?: string;
  page?: number;
  per_page?: number;
}

export const exceptionsApi = {
  /** List asset exceptions */
  list: (params?: ExceptionListParams) => {
    const searchParams = new URLSearchParams();
    if (params?.asset_type) searchParams.set("asset_type", params.asset_type);
    if (params?.search) searchParams.set("search", params.search);
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.per_page) searchParams.set("per_page", params.per_page.toString());
    return fetchWithAuth<AssetExceptionListResponse>(`/assets/exceptions?${searchParams}`);
  },

  /** Create an asset exception */
  create: (data: CreateExceptionParams) =>
    fetchWithAuth<AssetException>("/assets/exceptions", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  /** Get a single exception */
  get: (id: string) => fetchWithAuth<AssetException>(`/assets/exceptions/${id}`),

  /** Update an exception */
  update: (id: string, data: UpdateExceptionParams) =>
    fetchWithAuth<AssetException>(`/assets/exceptions/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  /** Delete an exception */
  delete: (id: string) =>
    fetchWithAuth<void>(`/assets/exceptions/${id}`, { method: "DELETE" }),

  /** Create exception from an existing asset (convenience) */
  exceptAsset: (assetId: string, data: ExceptAssetParams) =>
    fetchWithAuth<AssetException>(`/assets/${assetId}/except`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
};

// Graph Visualization Types
export interface GraphNode {
  id: string;
  node_type: "domain" | "subdomain" | "ip" | "port";
  label: string;
  properties: Record<string, unknown>;
  status: "active" | "stale" | "removed";
  visibility?: "known" | "unknown" | "rogue";
  risk_score?: number;
}

export interface GraphEdge {
  source: string;
  target: string;
  edge_type: "parent_of" | "cname_to" | "contains";
}

export interface NodeCounts {
  subdomains: number;
  ips: number;
  ports: number;
  domains: number;
}

export interface GraphMetadata {
  total_nodes: number;
  total_edges: number;
  node_counts: NodeCounts;
  domain_id?: string;
  domain_name?: string;
}

export interface GraphData {
  nodes: GraphNode[];
  edges: GraphEdge[];
  metadata: GraphMetadata;
}

export interface GraphParams {
  domain_id?: string;
  status?: string;
  include_ports?: boolean;
  max_nodes?: number;
  asset_types?: string;
}

export const graphApi = {
  /** Get asset graph data for visualization */
  getGraph: (params?: GraphParams) => {
    const searchParams = new URLSearchParams();
    if (params?.domain_id) searchParams.set("domain_id", params.domain_id);
    if (params?.status) searchParams.set("status", params.status);
    if (params?.include_ports !== undefined) searchParams.set("include_ports", String(params.include_ports));
    if (params?.max_nodes) searchParams.set("max_nodes", params.max_nodes.toString());
    if (params?.asset_types) searchParams.set("asset_types", params.asset_types);
    return fetchWithAuth<GraphData>(`/assets/graph?${searchParams}`);
  },

  /** Get graph data for a specific domain */
  getDomainGraph: (domainId: string) =>
    fetchWithAuth<GraphData>(`/domains/${domainId}/assets/graph`),
};

// Risk Dashboard Types
export interface CveRisk {
  cve_id: string;
  severity: string;
  epss_score?: number;
  is_kev: boolean;
  affected_port: number;
  affected_ip: string;
  service_name?: string;
  service_version?: string;
  asset_id: string;
}

export interface TakeoverRisk {
  asset_id: string;
  fqdn: string;
  takeover_risk: string;
  takeover_reason?: string;
  cname_target?: string;
  http_status?: number;
  last_seen_at: string;
}

export interface RiskAssetSummary {
  id: string;
  asset_type: string;
  identifier: string;
  status: string;
  visibility?: string;
  first_seen_at: string;
  last_seen_at: string;
  is_cloud?: boolean;
  cloud_provider?: string;
  cloud_region?: string;
}

export interface ServiceRisk {
  asset_id: string;
  ip_address: string;
  port: number;
  protocol: string;
  service_name?: string;
  service_version?: string;
  is_encrypted: boolean;
  state: string;
  risk_reason: string;
  is_cloud?: boolean;
  cloud_provider?: string;
  cloud_region?: string;
}

export interface RiskStats {
  total_critical_cves: number;
  total_takeover_risks: number;
  total_shadow_assets: number;
  total_stale_assets: number;
  total_exposed_services: number;
}

export interface RiskSummary {
  critical_cves: CveRisk[];
  takeover_risks: TakeoverRisk[];
  shadow_assets: RiskAssetSummary[];
  stale_assets: RiskAssetSummary[];
  exposed_services: ServiceRisk[];
  stats: RiskStats;
}

export interface TimelineEvent {
  id: string;
  timestamp: string;
  event_type: string;
  asset_type: string;
  identifier: string;
  asset_id: string;
  details: Record<string, unknown>;
  severity: string;
}

export interface TimelineResponse {
  events: TimelineEvent[];
  total: number;
}

export interface PortCell {
  port: number;
  state: string;
  service?: string;
  service_version?: string;
  service_banner?: string;
  protocol: string;
  encrypted: boolean;
  cve_count: number;
  highest_severity?: string;
  asset_id?: string;
}

export interface IpRow {
  ip: string;
  hostname?: string;
  asset_id: string;
  subdomains: string[];
  is_cloud: boolean;
  cloud_provider?: string;
  asn_name?: string;
  country_code?: string;
  cells: PortCell[];
}

export interface ServiceMatrix {
  ips: IpRow[];
  ports: number[];
}

export interface AttackPathService {
  port: number;
  protocol: string;
  service_name?: string;
  service_version?: string;
}

export interface AttackPathCve {
  cve_id: string;
  severity: string;
  epss_score?: number;
  is_kev: boolean;
  description?: string;
}

export interface AttackPath {
  id: string;
  severity: string;
  entry_point: string;
  entry_point_type: string;
  service: AttackPathService;
  vulnerability?: AttackPathCve;
  remediation: string;
  is_cloud?: boolean;
  cloud_provider?: string;
  cloud_region?: string;
}

export interface AttackPathsResponse {
  paths: AttackPath[];
  total: number;
}

export const riskApi = {
  /** Get aggregated risk summary */
  getRiskSummary: (params?: { limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set("limit", params.limit.toString());
    return fetchWithAuth<RiskSummary>(`/assets/risks?${searchParams}`);
  },

  /** Get asset change timeline */
  getTimeline: (params?: { days?: number; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.days) searchParams.set("days", params.days.toString());
    if (params?.limit) searchParams.set("limit", params.limit.toString());
    return fetchWithAuth<TimelineResponse>(`/assets/timeline?${searchParams}`);
  },

  /** Get service exposure matrix */
  getServiceMatrix: () => fetchWithAuth<ServiceMatrix>(`/assets/matrix`),

  /** Get potential attack paths */
  getAttackPaths: (params?: { limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set("limit", params.limit.toString());
    return fetchWithAuth<AttackPathsResponse>(`/assets/attack-paths?${searchParams}`);
  },
};
